import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { notificationsApi } from '@/api/notificationsApi';
import type {
    Notification,
    NotificationsState,
    NotificationsListRequest,
    NotificationCategory,
    NotificationType,
} from '@/types/notifications';

// Начальное состояние
const initialState: NotificationsState = {
    notifications: [],
    unreadCount: 0,
    loading: false,
    error: null,
    hasMore: true,
    currentOffset: 0,
    filters: {},
};

// Async Thunks
/**
 * Загрузка уведомлений с сервера
 */
export const fetchNotifications = createAsyncThunk(
    'notifications/fetchNotifications',
    async (params: NotificationsListRequest = {}, { rejectWithValue }) => {
        try {
            const response = await notificationsApi.getNotifications({
                limit: 20,
                ...params,
            });
            return response;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch notifications');
        }
    }
);

/**
 * Загрузка счетчика непрочитанных
 */
export const fetchUnreadCount = createAsyncThunk(
    'notifications/fetchUnreadCount',
    async (_, { rejectWithValue }) => {
        try {
            const count = await notificationsApi.getUnreadCount();
            return count;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to fetch unread count');
        }
    }
);

/**
 * Отметить уведомление как прочитанное
 */
export const markNotificationAsRead = createAsyncThunk(
    'notifications/markAsRead',
    async (notificationId: number, { rejectWithValue }) => {
        try {
            await notificationsApi.markAsRead(notificationId);
            return notificationId;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to mark as read');
        }
    }
);

/**
 * Отметить все уведомления как прочитанные
 */
export const markAllNotificationsAsRead = createAsyncThunk(
    'notifications/markAllAsRead',
    async (_, { rejectWithValue }) => {
        try {
            await notificationsApi.markAllAsRead();
            return true;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to mark all as read');
        }
    }
);

/**
 * Удалить уведомление
 */
export const deleteNotification = createAsyncThunk(
    'notifications/deleteNotification',
    async (notificationId: number, { rejectWithValue }) => {
        try {
            await notificationsApi.deleteNotification(notificationId);
            return notificationId;
        } catch (error: any) {
            return rejectWithValue(error.message || 'Failed to delete notification');
        }
    }
);

// Slice
const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        // Добавить новое уведомление (например, через WebSocket)
        addNotification: (state, action: PayloadAction<Notification>) => {
            state.notifications.unshift(action.payload);
            if (!action.payload.is_read) {
                state.unreadCount += 1;
            }
        },

        // Обновить уведомление
        updateNotification: (state, action: PayloadAction<Notification>) => {
            const index = state.notifications.findIndex(n => n.id === action.payload.id);
            if (index !== -1) {
                state.notifications[index] = action.payload;
            }
        },

        // Умное слияние уведомлений (без дубликатов)
        mergeNotifications: (state, action: PayloadAction<Notification[]>) => {
            const existingIds = new Set(state.notifications.map(n => n.id));
            const newNotifications = action.payload.filter(n => !existingIds.has(n.id));

            if (newNotifications.length > 0) {
                state.notifications = [...state.notifications, ...newNotifications];
            }

            // Обновляем существующие
            action.payload.forEach(newNotif => {
                const index = state.notifications.findIndex(n => n.id === newNotif.id);
                if (index !== -1) {
                    state.notifications[index] = newNotif;
                }
            });
        },

        // Установить фильтры
        setFilters: (state, action: PayloadAction<{
            isRead?: boolean;
            category?: NotificationCategory;
            type?: NotificationType;
        }>) => {
            state.filters = action.payload;
            state.currentOffset = 0;
            state.hasMore = true;
        },

        // Очистить фильтры
        clearFilters: (state) => {
            state.filters = {};
            state.currentOffset = 0;
            state.hasMore = true;
        },

        // Сбросить состояние
        resetNotifications: (state) => {
            state.notifications = [];
            state.currentOffset = 0;
            state.hasMore = true;
            state.error = null;
        },

        // Обновить счетчик непрочитанных
        setUnreadCount: (state, action: PayloadAction<number>) => {
            state.unreadCount = action.payload;
        },

        // Декремент счетчика непрочитанных
        decrementUnreadCount: (state) => {
            if (state.unreadCount > 0) {
                state.unreadCount -= 1;
            }
        },
    },
    extraReducers: (builder) => {
        // Загрузка уведомлений
        builder
            .addCase(fetchNotifications.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchNotifications.fulfilled, (state, action) => {
                state.loading = false;

                const data = action.payload.data || [];

                if (action.meta.arg.offset === 0) {
                    // Первая загрузка - заменяем список
                    state.notifications = data;
                } else {
                    // Пагинация - добавляем к существующим
                    state.notifications = [...state.notifications, ...data];
                }

                state.hasMore = action.payload.has_more;
                state.currentOffset = (action.meta.arg.offset || 0) + data.length;
            })
            .addCase(fetchNotifications.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Загрузка счетчика
        builder
            .addCase(fetchUnreadCount.fulfilled, (state, action) => {
                state.unreadCount = action.payload;
            });

        // Отметить как прочитанное
        builder
            .addCase(markNotificationAsRead.fulfilled, (state, action) => {
                const notification = state.notifications.find(n => n.id === action.payload);
                if (notification && !notification.is_read) {
                    notification.is_read = true;
                    notification.read_at = Math.floor(Date.now() / 1000); // Unix timestamp
                    state.unreadCount = Math.max(0, state.unreadCount - 1);
                }
            });

        // Отметить все как прочитанные
        builder
            .addCase(markAllNotificationsAsRead.fulfilled, (state) => {
                const now = Math.floor(Date.now() / 1000); // Unix timestamp
                state.notifications.forEach(notification => {
                    if (!notification.is_read) {
                        notification.is_read = true;
                        notification.read_at = now;
                    }
                });
                state.unreadCount = 0;
            });

        // Удалить уведомление
        builder
            .addCase(deleteNotification.fulfilled, (state, action) => {
                const index = state.notifications.findIndex(n => n.id === action.payload);
                if (index !== -1) {
                    const notification = state.notifications[index];
                    if (!notification.is_read) {
                        state.unreadCount = Math.max(0, state.unreadCount - 1);
                    }
                    state.notifications.splice(index, 1);
                }
            });
    },
});

// Экспорт actions
export const {
    addNotification,
    updateNotification,
    mergeNotifications,
    setFilters,
    clearFilters,
    resetNotifications,
    setUnreadCount,
    decrementUnreadCount,
} = notificationsSlice.actions;

// Экспорт reducer
export const notificationsReducer = notificationsSlice.reducer;

// Селекторы
export const selectNotifications = (state: { notifications: NotificationsState }) =>
    state.notifications.notifications;

export const selectUnreadCount = (state: { notifications: NotificationsState }) =>
    state.notifications.unreadCount;

export const selectNotificationsLoading = (state: { notifications: NotificationsState }) =>
    state.notifications.loading;

export const selectNotificationsError = (state: { notifications: NotificationsState }) =>
    state.notifications.error;

export const selectHasMore = (state: { notifications: NotificationsState }) =>
    state.notifications.hasMore;

export const selectCurrentOffset = (state: { notifications: NotificationsState }) =>
    state.notifications.currentOffset;

export const selectFilters = (state: { notifications: NotificationsState }) =>
    state.notifications.filters;
